'use client'

import './auth-hooks'
import './request-headers'
import * as React from 'react'
import { LazyMotion, domAnimation } from 'framer-motion'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { DefaultOptions } from '@tanstack/react-query'
import { checkAndHandleAuth } from '@repo/studio-utils/utils/auth'
import { DarkModeProvider } from '@repo/studio-components/context/dark-mode/dark-mode.context'
import { CoinTransferAnimationProvider } from '@repo/studio-hooks-coins/hooks/use-coin-transfer-animation'
import { StudioLayout } from '@repo/studio-components-studio-layout'
import { useScrollToTopOnNavigate } from '@repo/studio-components-studio-layout/hooks/use-scroll-to-top-on-navigate'
import { StudioConfigProvider } from '@repo/studio-config/studio-config-context'
import { WorkspaceSwitcher } from '@repo/studio-features-workspace/workspace-switcher'
import { WorkspaceSwitchFeedbackHost } from '@repo/studio-components-workspace-switcher/workspace-switch-feedback'
import { PLATFORM_WORKSPACE } from '@repo/studio-features-workspace/workspaces'
import type { StudioWorkspace } from '@repo/studio-features-workspace/workspaces'
import { nextMatchPath } from './next-routing-nav'
import {
  NextLinkComponent,
  useNextCurrentLocationState,
  useNextCurrentPathname,
  useNextNavigateTo,
} from './next-routing'
import { LocaleUrlSync } from './locale-url-sync'
import { GrowthBookProvider } from 'growthbook'
import { UserAuthModalProvider } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { UserAuthDialogClient } from './user-auth-dialog-client'
import { StudioReactI18nProvider } from './studio-react-i18n-provider'
import { AidaDialogProvider } from '@repo/studio-features-aida/dialog-provider'
import { PaymentsDialogProvider } from 'payments/components/payments-dialog-wrapper-context'
import { PaymentsCompleteScreenDialog } from 'payments/payments-dialog/components/payments-complete-screen-dialog-modal'
import { CookiesProvider } from 'react-cookie'
import type { StudioConfig } from '@repo/studio-config/studio-config-context'
import { createFlatStudioTranslate } from '@repo/studio-i18n-hooks/shell-translations'
import { RegistrationSource } from '@repo/auth-utils'

function NoStudioBanners() {
  return null
}

const PLATFORM_LAYOUT_COMPONENTS = { StudioBanners: NoStudioBanners }

function ScrollToTopOnNavigate() {
  useScrollToTopOnNavigate()
  return null
}

const STATIC_CONFIG = {
  LinkComponent: NextLinkComponent,
  navigation: 'none',
  referralBaseUrl: process.env.VITE_STUDIO_URL,
  useCurrentPathname: useNextCurrentPathname,
  useCurrentLocationState: useNextCurrentLocationState,
  useNavigateTo: useNextNavigateTo,
  matchPath: nextMatchPath,
} satisfies Omit<StudioConfig, 't' | 'logoSlot'>

interface StudioChromeProvidersProps {
  children: React.ReactNode
  shellTranslations: Record<string, string>
  currentWorkspace?: StudioWorkspace
  queryDefaultOptions?: DefaultOptions
}

export function StudioChromeProviders({
  children,
  shellTranslations,
  currentWorkspace = PLATFORM_WORKSPACE,
  queryDefaultOptions,
}: StudioChromeProvidersProps) {
  const [queryClient] = React.useState(
    () => new QueryClient({ defaultOptions: queryDefaultOptions }),
  )

  React.useEffect(() => {
    void checkAndHandleAuth(queryClient)
  }, [queryClient])

  const t = React.useMemo(() => createFlatStudioTranslate(shellTranslations), [shellTranslations])

  const studioConfig: StudioConfig = React.useMemo(
    () => ({
      ...STATIC_CONFIG,
      t,
      logoSlot: <WorkspaceSwitcher current={currentWorkspace} />,
    }),
    [currentWorkspace, t],
  )

  return (
    <CookiesProvider>
      <LazyMotion features={domAnimation} strict>
        <QueryClientProvider client={queryClient}>
          <StudioConfigProvider config={studioConfig}>
            <LocaleUrlSync />
            <GrowthBookProvider options={{ streaming: true }}>
              <DarkModeProvider forcedMode="dark">
                <PaymentsDialogProvider appType="studio">
                  <PaymentsCompleteScreenDialog />
                  <WorkspaceSwitchFeedbackHost />
                  <CoinTransferAnimationProvider>
                    <UserAuthModalProvider
                      registrationSource={RegistrationSource.Studio}
                      skipReloadPageOnSuccess
                      trackingOptions={{ registrationConversion: true }}
                    >
                      <StudioReactI18nProvider shellTranslations={shellTranslations}>
                        <AidaDialogProvider ssrSafe>
                          <StudioLayout navigation="none" _components={PLATFORM_LAYOUT_COMPONENTS}>
                            <ScrollToTopOnNavigate />
                            {children}
                          </StudioLayout>
                          <UserAuthDialogClient bodyClassName="text-aubergine-500 font-poppins" />
                        </AidaDialogProvider>
                      </StudioReactI18nProvider>
                    </UserAuthModalProvider>
                  </CoinTransferAnimationProvider>
                </PaymentsDialogProvider>
              </DarkModeProvider>
            </GrowthBookProvider>
          </StudioConfigProvider>
        </QueryClientProvider>
      </LazyMotion>
    </CookiesProvider>
  )
}
